/**
 * Settings → "Storage" (F1). Surfaces the time-series database footprint
 * (size, row count, distinct series, oldest reading, an estimated 1-year
 * growth figure) and gives the operator two ways to reclaim space:
 *
 *   1. **Manual prune** — "delete data older than 3 mo / 6 mo / 1 yr /
 *      custom date", gated behind a destructive-action confirm modal.
 *      Calls `POST /api/history/prune` (which VACUUMs when rows are
 *      removed) via `historyApi.prune`.
 *   2. **Auto-retention** — a policy (`off` | `keepDays`) persisted on the
 *      Pi at `./data/retention.json` and enforced by the agent's daily
 *      retention job. Read/written via `historyApi.getRetention` /
 *      `historyApi.setRetention`.
 *
 * Stats load on mount through `historyApi.getStats` and refresh after any
 * successful prune so the gauge reflects freed space immediately.
 */
import { useCallback, useEffect, useState } from 'react';

import type { RetentionPolicy, TimeSeriesStats } from '@reefapp/shared';

import { Button } from '../../components/Button';
import { Card, CardBody, CardHeader, CardTitle } from '../../components/Card';
import { historyApi } from '../../lib/history-api';

type Status =
  | { kind: 'idle' }
  | { kind: 'working'; message: string }
  | { kind: 'ok'; message: string }
  | { kind: 'error'; message: string };

type PrunePeriod = '3mo' | '6mo' | '1yr' | 'custom';

const DAY_MS = 24 * 60 * 60 * 1_000;

const PERIOD_DAYS: Record<Exclude<PrunePeriod, 'custom'>, number> = {
  '3mo': 90,
  '6mo': 180,
  '1yr': 365,
};

const PERIOD_LABEL: Record<PrunePeriod, string> = {
  '3mo': '3 months',
  '6mo': '6 months',
  '1yr': '1 year',
  custom: 'a custom date',
};

function formatBytes(bytes: number): string {
  if (bytes <= 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const exp = Math.min(units.length - 1, Math.floor(Math.log(bytes) / Math.log(1024)));
  const value = bytes / 1024 ** exp;
  return `${value.toFixed(value >= 100 || exp === 0 ? 0 : 1)} ${units[exp]}`;
}

function formatDate(ts: number | null): string {
  if (ts === null) return '—';
  return new Date(ts).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

/** Project a 1-year on-disk size from the current fill rate. */
function estimateYearSize(stats: TimeSeriesStats): string {
  if (
    stats.rowCount === 0 ||
    stats.oldestTs === null ||
    stats.newestTs === null ||
    stats.newestTs <= stats.oldestTs
  ) {
    return '—';
  }
  const spanDays = (stats.newestTs - stats.oldestTs) / DAY_MS;
  if (spanDays < 0.5) return '—';
  const bytesPerDay = stats.approxBytes / spanDays;
  return `~${formatBytes(bytesPerDay * 365)}/yr`;
}

/** Convert a YYYY-MM-DD `<input type="date">` value to local-midnight ms. */
function dateInputToMs(value: string): number | null {
  if (!value) return null;
  const parts = value.split('-').map((s) => Number.parseInt(s, 10));
  if (parts.length !== 3 || parts.some((n) => Number.isNaN(n))) return null;
  const [y, m, d] = parts as [number, number, number];
  return new Date(y, m - 1, d, 0, 0, 0, 0).getTime();
}

export function StorageSection() {
  const [stats, setStats] = useState<TimeSeriesStats | null>(null);
  const [statsError, setStatsError] = useState<string | null>(null);
  const [statsLoading, setStatsLoading] = useState(true);

  const [period, setPeriod] = useState<PrunePeriod>('3mo');
  const [customDate, setCustomDate] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [status, setStatus] = useState<Status>({ kind: 'idle' });

  const [policy, setPolicy] = useState<RetentionPolicy>({ mode: 'off' });
  const [retentionDays, setRetentionDays] = useState(90);
  const [retentionStatus, setRetentionStatus] = useState<Status>({ kind: 'idle' });

  const loadStats = useCallback(async () => {
    setStatsLoading(true);
    const res = await historyApi.getStats();
    if (res.ok) {
      setStats(res.value);
      setStatsError(null);
    } else {
      setStatsError(res.error);
    }
    setStatsLoading(false);
  }, []);

  useEffect(() => {
    void loadStats();
  }, [loadStats]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const res = await historyApi.getRetention();
      if (cancelled) return;
      if (res.ok) {
        setPolicy(res.value);
        if (res.value.mode === 'keepDays') setRetentionDays(res.value.keepDays);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  /** Resolve the `beforeTs` cutoff for the selected prune period. */
  function resolveCutoff(): number | null {
    if (period === 'custom') return dateInputToMs(customDate);
    return Date.now() - PERIOD_DAYS[period] * DAY_MS;
  }

  function openConfirm() {
    if (period === 'custom' && resolveCutoff() === null) {
      setStatus({ kind: 'error', message: 'Pick a valid date first.' });
      return;
    }
    setStatus({ kind: 'idle' });
    setConfirmOpen(true);
  }

  async function handlePruneConfirmed() {
    const cutoff = resolveCutoff();
    setConfirmOpen(false);
    if (cutoff === null) {
      setStatus({ kind: 'error', message: 'Pick a valid date first.' });
      return;
    }
    setStatus({ kind: 'working', message: 'Deleting old readings…' });
    const res = await historyApi.prune(cutoff);
    if (!res.ok) {
      setStatus({ kind: 'error', message: `Delete failed: ${res.error}` });
      return;
    }
    setStatus({
      kind: 'ok',
      message: `Deleted ${res.value.deleted.toLocaleString()} reading${
        res.value.deleted === 1 ? '' : 's'
      } and reclaimed space.`,
    });
    await loadStats();
  }

  const retentionMode: RetentionPolicy['mode'] = policy.mode;

  async function handleSaveRetention() {
    const next: RetentionPolicy =
      retentionMode === 'keepDays'
        ? { mode: 'keepDays', keepDays: retentionDays }
        : { mode: 'off' };
    setRetentionStatus({ kind: 'working', message: 'Saving…' });
    const res = await historyApi.setRetention(next);
    if (!res.ok) {
      setRetentionStatus({ kind: 'error', message: `Save failed: ${res.error}` });
      return;
    }
    setPolicy(res.value);
    if (res.value.mode === 'keepDays') setRetentionDays(res.value.keepDays);
    setRetentionStatus({
      kind: 'ok',
      message:
        res.value.mode === 'off'
          ? 'Auto-cleanup off — all history is kept.'
          : `Auto-cleanup on — keeping the last ${res.value.keepDays} days.`,
    });
  }

  const working = status.kind === 'working';

  return (
    <Card className="mt-6" data-testid="settings-storage-section">
      <CardHeader>
        <CardTitle>Storage</CardTitle>
      </CardHeader>
      <CardBody>
        <p className="text-xs text-reef-300 mb-4">
          Your sensor history is stored on the controller. This panel shows how
          much space it uses and lets you free space by deleting old readings —
          either manually or automatically.
        </p>

        {/* Stats gauge */}
        {statsLoading ? (
          <p className="text-sm text-reef-400" data-testid="storage-loading">
            Loading storage stats…
          </p>
        ) : statsError ? (
          <p className="text-sm text-red-400" data-testid="storage-error">
            Could not load storage stats: {statsError}
          </p>
        ) : stats ? (
          <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm text-reef-200">
            <dt className="text-reef-400">Database size</dt>
            <dd data-testid="storage-db-size" className="tabular-nums text-reef-50">
              {formatBytes(stats.approxBytes)}
            </dd>
            <dt className="text-reef-400">Total readings</dt>
            <dd data-testid="storage-row-count" className="tabular-nums text-reef-50">
              {stats.rowCount.toLocaleString()}
            </dd>
            <dt className="text-reef-400">Distinct signals</dt>
            <dd data-testid="storage-series-count" className="tabular-nums text-reef-50">
              {stats.seriesCount.toLocaleString()}
            </dd>
            <dt className="text-reef-400">Oldest reading</dt>
            <dd data-testid="storage-oldest" className="text-reef-50">
              {formatDate(stats.oldestTs)}
            </dd>
            <dt className="text-reef-400">Est. growth</dt>
            <dd data-testid="storage-capacity" className="text-reef-50">
              {estimateYearSize(stats)}
            </dd>
          </dl>
        ) : null}

        <hr className="my-5 border-reef-700/50" />

        {/* Manual prune */}
        <p className="text-xs text-reef-300 mb-2">
          Delete readings older than a cutoff. This permanently removes old
          history and reclaims disk space.
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <label className="text-sm text-reef-200">
            Delete data older than{' '}
            <select
              value={period}
              onChange={(e) => setPeriod(e.target.value as PrunePeriod)}
              disabled={working}
              data-testid="storage-prune-period"
              className="rounded border border-reef-700 bg-reef-900 px-2 py-1 text-reef-100"
            >
              <option value="3mo">3 months</option>
              <option value="6mo">6 months</option>
              <option value="1yr">1 year</option>
              <option value="custom">a custom date…</option>
            </select>
          </label>

          {period === 'custom' ? (
            <input
              type="date"
              value={customDate}
              onChange={(e) => setCustomDate(e.target.value)}
              disabled={working}
              data-testid="storage-prune-custom"
              className="rounded border border-reef-700 bg-reef-900 px-2 py-1 text-sm text-reef-100"
            />
          ) : null}

          <Button
            variant="primary"
            size="sm"
            onClick={openConfirm}
            disabled={working}
            data-testid="storage-prune"
          >
            Delete old data
          </Button>
        </div>

        {status.kind !== 'idle' ? (
          <p
            className={`mt-3 text-sm ${
              status.kind === 'error'
                ? 'text-red-400'
                : status.kind === 'ok'
                  ? 'text-green-400'
                  : 'text-reef-300'
            }`}
            data-testid="storage-prune-status"
            role="status"
          >
            {status.message}
          </p>
        ) : null}

        <hr className="my-5 border-reef-700/50" />

        {/* Auto-retention */}
        <p className="text-xs text-reef-300 mb-2">
          Automatic cleanup runs daily on the controller and keeps your
          database from growing forever.
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <label className="text-sm text-reef-200">
            Auto-cleanup{' '}
            <select
              value={retentionMode}
              onChange={(e) =>
                setPolicy(
                  e.target.value === 'keepDays'
                    ? { mode: 'keepDays', keepDays: retentionDays }
                    : { mode: 'off' },
                )
              }
              data-testid="storage-retention-mode"
              className="rounded border border-reef-700 bg-reef-900 px-2 py-1 text-reef-100"
            >
              <option value="off">Off — keep everything</option>
              <option value="keepDays">Keep recent days only</option>
            </select>
          </label>

          {retentionMode === 'keepDays' ? (
            <label className="text-sm text-reef-200">
              Keep last{' '}
              <input
                type="number"
                min={1}
                value={retentionDays}
                onChange={(e) => {
                  const n = Number.parseInt(e.target.value, 10);
                  if (!Number.isNaN(n) && n > 0) {
                    setRetentionDays(n);
                    setPolicy({ mode: 'keepDays', keepDays: n });
                  }
                }}
                data-testid="storage-retention-days"
                className="w-20 rounded border border-reef-700 bg-reef-900 px-2 py-1 text-reef-100"
              />{' '}
              days
            </label>
          ) : null}

          <Button
            variant="ghost"
            size="sm"
            onClick={() => void handleSaveRetention()}
            disabled={retentionStatus.kind === 'working'}
            data-testid="storage-retention-save"
          >
            Save policy
          </Button>
        </div>

        {retentionStatus.kind !== 'idle' ? (
          <p
            className={`mt-3 text-sm ${
              retentionStatus.kind === 'error'
                ? 'text-red-400'
                : retentionStatus.kind === 'ok'
                  ? 'text-green-400'
                  : 'text-reef-300'
            }`}
            data-testid="storage-retention-status"
            role="status"
          >
            {retentionStatus.message}
          </p>
        ) : null}
      </CardBody>

      {confirmOpen ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4"
          data-testid="storage-prune-dialog"
          role="dialog"
          aria-modal="true"
        >
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle>Delete old readings?</CardTitle>
            </CardHeader>
            <CardBody>
              <p className="text-sm text-reef-200">
                This permanently deletes all sensor readings older than{' '}
                <span className="text-reef-100">{PERIOD_LABEL[period]}</span>
                {period === 'custom' && customDate ? (
                  <>
                    {' '}
                    (<span className="text-reef-100">{customDate}</span>)
                  </>
                ) : null}
                . This cannot be undone.
              </p>
              <div className="mt-4 flex items-center justify-end gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setConfirmOpen(false)}
                  data-testid="storage-prune-cancel"
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => void handlePruneConfirmed()}
                  data-testid="storage-prune-confirm"
                >
                  Delete old data
                </Button>
              </div>
            </CardBody>
          </Card>
        </div>
      ) : null}
    </Card>
  );
}
